"use client";

import React from "react";
import { Droplet, Sparkles, Heart } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
const classicFlavors = ["Irish Cream", "Coconut", "Salted Caramel", "Vanilla", "Caramel", "Mocha", "Almond", "Toasted Marshmallow", "French Vanilla", "Hazelnut", "Pistachio"];
const sugarFreeFlavors = ["Caramel", "Hazelnut"];
const houseMadeFlavors = ["Cookie Butter", "Carrot Cake", "Simple Syrup", "Banana Bread", "Blueberry"];
export default function Syrups() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: shouldReduceMotion ? 0 : custom * 0.1,
        ease: "easeOut"
      }
    })
  };
  return <section id="syrup-flavors" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center mb-14 md:mb-18">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-foreground">
            Syrup Flavors
          </h2>
          <p className="mt-3.5 text-base sm:text-lg text-muted-foreground">
            Customize any coffee, matcha, or iced beverage with your favorite taste.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-start">
          {/* Classic Flavors Card */}
          <motion.div custom={0} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} variants={cardVariants} className="rounded-2xl border border-border bg-card text-card-foreground p-6 sm:p-8 shadow-xs hover:border-secondary/40 transition-[border-color,box-shadow] duration-300">
            <div className="flex items-center gap-3.5 mb-6 pb-5 border-b border-border">
              <div className="flex size-10 items-center justify-center rounded-xl bg-accent/60 text-foreground">
                <Droplet className="size-5" />
              </div>
              <div>
                <h3 className="font-serif text-xl sm:text-2xl font-medium text-card-foreground">
                  Classic Flavors
                </h3>
                <span className="text-xs text-muted-foreground font-sans">
                  Time-honored staples
                </span>
              </div>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-y-2.5">
              {classicFlavors.map((flavor, index) => <li key={flavor} data-index={index} className="flex items-center text-sm sm:text-base text-card-foreground/90 font-sans">
                  <span className="mr-2.5 size-1.5 rounded-full bg-secondary" />
                  <span>{flavor}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Sugar-Free Card */}
          <motion.div custom={1} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} variants={cardVariants} className="rounded-2xl border border-border bg-card text-card-foreground p-6 sm:p-8 shadow-xs hover:border-secondary/40 transition-[border-color,box-shadow] duration-300">
            <div className="flex items-center gap-3.5 mb-6 pb-5 border-b border-border">
              <div className="flex size-10 items-center justify-center rounded-xl bg-accent/60 text-foreground">
                <Sparkles className="size-5" />
              </div>
              <div>
                <h3 className="font-serif text-xl sm:text-2xl font-medium text-card-foreground">
                  Sugar-Free
                </h3>
                <span className="text-xs text-muted-foreground font-sans">
                  Zero added sugar
                </span>
              </div>
            </div>

            <ul className="flex flex-col gap-y-2.5">
              {sugarFreeFlavors.map((flavor, index) => <li key={flavor} data-index={index} className="flex items-center text-sm sm:text-base text-card-foreground/90 font-sans">
                  <span className="mr-2.5 size-1.5 rounded-full bg-secondary" />
                  <span>{flavor}</span>
                </li>)}
            </ul>

            <div className="mt-8 rounded-xl bg-accent/40 p-4 border border-border/70">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Sweetened to pair smoothly with our house espresso without overpowering the roast.
              </p>
            </div>
          </motion.div>

          {/* House-Made Card */}
          <motion.div custom={2} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} variants={cardVariants} className="rounded-2xl border border-secondary/30 bg-accent/20 text-card-foreground p-6 sm:p-8 shadow-xs hover:border-secondary/60 transition-[border-color,box-shadow] duration-300 relative overflow-hidden">
            <div className="flex items-start justify-between gap-2 mb-6 pb-5 border-b border-border">
              <div className="flex items-center gap-3.5">
                <div className="flex size-10 items-center justify-center rounded-xl bg-secondary text-secondary-foreground">
                  <Heart className="size-5" />
                </div>
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl font-medium text-card-foreground">
                    House-Made
                  </h3>
                  <span className="text-xs text-muted-foreground font-sans">
                    Small batch recipes
                  </span>
                </div>
              </div>
              <Badge variant="secondary" className="font-sans text-xs px-2.5 py-0.5 rounded-full shadow-2xs">
                Made in-house
              </Badge>
            </div>

            <ul className="flex flex-col gap-y-2.5">
              {houseMadeFlavors.map((flavor, index) => <li key={flavor} data-index={index} className="flex items-center text-sm sm:text-base text-card-foreground/90 font-sans">
                  <span className="mr-2.5 size-1.5 rounded-full bg-secondary" />
                  <span className="font-medium">{flavor}</span>
                </li>)}
            </ul>

            <div className="mt-8 rounded-xl bg-background/80 p-4 border border-border/70">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Simmered in our café kitchen using real spices, pure cane sugar, and seasonal fruit purees.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}